<script setup lang="ts">
import { ref, watch, computed } from "vue";
import Modal from "@/ui/components/modal.vue";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import AlertUsagesDO = App.Data.Alerts.AlertUsagesDO;

const isVisible = defineModel<boolean>();

const props = defineProps<{
	alertId: number | null;
	alertTitle: string;
}>();

const emit = defineEmits(["deleted"]);

const isLoading = ref(false);
const usages = ref<AlertUsagesDO>({ triggers: [], visibility: [] });

const isBlockedByTriggers = computed(() => usages.value.triggers.length > 0);
const hasVisibilityUsage = computed(() => usages.value.visibility.length > 0);
const visibilityCardCount = computed(() => usages.value.visibility.length);

const loadUsages = async () => {
	if (props.alertId === null) {
		return;
	}

	isLoading.value = true;
	usages.value = { triggers: [], visibility: [] };

	const result = await useApiClient("alerts").usages(props.alertId);
	if (result.isOk()) {
		usages.value = result.value;
	}

	isLoading.value = false;
};

watch(isVisible, (visible) => {
	if (visible) {
		loadUsages();
	}
});

const confirmDelete = async () => {
	if (props.alertId === null) {
		return;
	}

	const response = await toastApiResult(
		useApiClient("alerts").delete(props.alertId)
	);

	if (response.isOk()) {
		emit("deleted");
	}

	isVisible.value = false;
};
</script>

<template>
	<Modal
		v-model:visible="isVisible"
		title="Delete Alert"
		icon="fa-duotone fa-trash"
	>
		<v-progress-linear v-if="isLoading" indeterminate height="2" />

		<div v-else style="font-size: 0.875rem">
			<template v-if="isBlockedByTriggers">
				<v-alert type="error">
					This alert triggers {{ usages.triggers.length }} export{{
						usages.triggers.length === 1 ? "" : "s"
					}}. These need to be removed before the alert can be deleted
				</v-alert>
				<v-table density="compact" class="mb-4">
					<thead>
						<tr>
							<th>Export</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="trigger in usages.triggers" :key="trigger.uuid">
							<td>
								<router-link
									:to="{
										name: 'scheduler.configuration',
										params: { schedule_uuid: trigger.uuid },
									}"
									target="_blank"
									class="text-primary"
								>
									{{ trigger.title }}
								</router-link>
							</td>
						</tr>
					</tbody>
				</v-table>
				<p></p>
			</template>

			<template v-if="hasVisibilityUsage">
				<v-alert type="warning" variant="tonal">
					This alert controls conditional rendering on
					{{ visibilityCardCount }} dashboard card{{
						visibilityCardCount === 1 ? "" : "s"
					}}. The conditional rendering will be removed if this alert is deleted
				</v-alert>
				<v-table density="compact">
					<thead>
						<tr>
							<th>Dashboard</th>
							<th>Card</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(usage, index) in usages.visibility" :key="index">
							<td>
								<router-link
									:to="{
										name: 'dashboards.builder',
										params: { dashboard_uuid: usage.dashboard_uuid },
									}"
									target="_blank"
									class="text-primary"
								>
									{{ usage.dashboard_title }}
								</router-link>
							</td>
							<td>{{ usage.document_title ?? "-" }}</td>
						</tr>
					</tbody>
				</v-table>
			</template>

			<p v-if="!isBlockedByTriggers && !hasVisibilityUsage">
				Are you sure you want to delete "{{ alertTitle }}"?
			</p>
		</div>

		<template #actions>
			<v-btn
				v-if="isBlockedByTriggers || hasVisibilityUsage"
				variant="text"
				prepend-icon="fa-regular fa-arrows-rotate"
				:loading="isLoading"
				@click="loadUsages"
			>
				Refresh
			</v-btn>
			<v-spacer />
			<v-btn @click="isVisible = false">
				{{ isBlockedByTriggers ? "Close" : "Cancel" }}
			</v-btn>
			<v-btn
				:disabled="isBlockedByTriggers || isLoading"
				variant="tonal"
				color="red"
				@click="confirmDelete"
			>
				Delete
			</v-btn>
		</template>
	</Modal>
</template>
